import {
  Currency,
  Pair,
  Token,
  TokenAmount,
} from '@pancakeswap/sdk'
import { Interface } from '@ethersproject/abi'
import { getCreate2Address } from '@ethersproject/address'
import { keccak256, pack } from '@ethersproject/solidity'
import { AddressZero } from '@ethersproject/constants'
import { useEffect, useMemo, useState } from 'react'
import IPancakePairABI from 'config/abi/IPancakePair.json'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { ROBINHOOD_MAINNET_CHAIN_ID } from 'config/constants/chains'
import { useMultipleContractSingleData } from '../state/multicall/hooks'
import { wrappedCurrency } from '../utils/wrappedCurrency'

const PAIR_INTERFACE = new Interface(IPancakePairABI)

const FACTORY_INTERFACE = new Interface([
  'function getPair(address tokenA, address tokenB) view returns (address pair)',
])

const ROBINHOOD_MAINNET_FACTORY =
  '0x630A7AAE6EED4A96a079b024E8D08D67A1466F1f'

const ROBINHOOD_MAINNET_PAIR_INIT_CODE_HASH =
  '0x48b827ccd4e43255a342ed401f3efd627e38d58bd0a59189afedccdac89f529b'

const originalPairGetAddress = Pair.getAddress.bind(Pair)

;(Pair as any).getAddress = (tokenA: Token, tokenB: Token): string => {
  if (
    Number(tokenA.chainId) !== ROBINHOOD_MAINNET_CHAIN_ID &&
    Number(tokenB.chainId) !== ROBINHOOD_MAINNET_CHAIN_ID
  ) {
    return originalPairGetAddress(tokenA, tokenB)
  }

  if (
    Number(tokenA.chainId) !== ROBINHOOD_MAINNET_CHAIN_ID ||
    Number(tokenB.chainId) !== ROBINHOOD_MAINNET_CHAIN_ID
  ) {
    throw new Error('Robinhood Mainnet pair tokens must use chain 4663')
  }

  const [token0, token1] = tokenA.sortsBefore(tokenB)
    ? [tokenA, tokenB]
    : [tokenB, tokenA]

  const salt = keccak256(
    ['bytes'],
    [pack(['address', 'address'], [token0.address, token1.address])],
  )

  return getCreate2Address(
    ROBINHOOD_MAINNET_FACTORY,
    salt,
    ROBINHOOD_MAINNET_PAIR_INIT_CODE_HASH,
  )
}

export enum PairState {
  LOADING,
  NOT_EXISTS,
  EXISTS,
  INVALID,
}

export function usePairs(
  currencies: [Currency | undefined, Currency | undefined][],
): [PairState, Pair | null][] {
  const { chainId, library } = useActiveWeb3React()

  const tokens = useMemo(
    () =>
      currencies.map(([currencyA, currencyB]) => {
        const tokenA = wrappedCurrency(currencyA, chainId)
        const tokenB = wrappedCurrency(currencyB, chainId)

        if (
          chainId === ROBINHOOD_MAINNET_CHAIN_ID &&
          (!tokenA || !tokenB)
        ) {
          console.warn('Skipping invalid Robinhood Mainnet pair', {
            currencyA,
            currencyB,
            tokenA,
            tokenB,
          })
        }

        return [tokenA, tokenB] as [
          Token | undefined,
          Token | undefined,
        ]
      }),
    [chainId, currencies],
  )

  /*
   * Legacy deterministic pair calculation for GRX Chain and
   * Robinhood Testnet. Robinhood Mainnet uses Factory.getPair()
   * because the Pancake SDK does not know its factory/init-code hash.
   */
  const pairAddresses = useMemo(
    () =>
      tokens.map(([tokenA, tokenB]) => {
        if (chainId === ROBINHOOD_MAINNET_CHAIN_ID) {
          return undefined
        }

        try {
          return tokenA && tokenB && !tokenA.equals(tokenB)
            ? Pair.getAddress(tokenA, tokenB)
            : undefined
        } catch (error: any) {
          console.error(
            'Pair address calculation failed',
            error?.message,
            `${tokenA?.address}-${tokenB?.address}`,
            `chainId: ${tokenA?.chainId}`,
          )

          return undefined
        }
      }),
    [chainId, tokens],
  )

  const multicallResults = useMultipleContractSingleData(
    pairAddresses,
    PAIR_INTERFACE,
    'getReserves',
  )

  const legacyPairResults = useMemo(() => {
    return multicallResults.map((result, index) => {
      const { result: reserves, loading } = result
      const [tokenA, tokenB] = tokens[index]

      if (loading) return [PairState.LOADING, null] as [PairState, Pair | null]

      if (!tokenA || !tokenB || tokenA.equals(tokenB)) {
        return [PairState.INVALID, null] as [PairState, Pair | null]
      }

      if (!reserves) {
        return [PairState.NOT_EXISTS, null] as [PairState, Pair | null]
      }

      const { reserve0, reserve1 } = reserves
      const [token0, token1] = tokenA.sortsBefore(tokenB)
        ? [tokenA, tokenB]
        : [tokenB, tokenA]

      return [
        PairState.EXISTS,
        new Pair(
          new TokenAmount(token0, reserve0.toString()),
          new TokenAmount(token1, reserve1.toString()),
        ),
      ] as [PairState, Pair | null]
    })
  }, [multicallResults, tokens])

  const [robinhoodMainnetResults, setRobinhoodMainnetResults] =
    useState<[PairState, Pair | null][]>([])

  useEffect(() => {
    let cancelled = false

    if (
      chainId !== ROBINHOOD_MAINNET_CHAIN_ID ||
      !library
    ) {
      setRobinhoodMainnetResults([])
      return () => {
        cancelled = true
      }
    }

    setRobinhoodMainnetResults(
      tokens.map(([tokenA, tokenB]) =>
        !tokenA || !tokenB || tokenA.equals(tokenB)
          ? [PairState.INVALID, null]
          : [PairState.LOADING, null],
      ),
    )

    const loadPairs = async () => {
      const loaded = await Promise.all(
        tokens.map(async ([tokenA, tokenB]) => {
          if (!tokenA || !tokenB || tokenA.equals(tokenB)) {
            return [PairState.INVALID, null] as [PairState, Pair | null]
          }

          try {
            const getPairData = FACTORY_INTERFACE.encodeFunctionData(
              'getPair',
              [tokenA.address, tokenB.address],
            )

            const getPairResult = await library.call({
              to: ROBINHOOD_MAINNET_FACTORY,
              data: getPairData,
            })

            const [pairAddress] =
              FACTORY_INTERFACE.decodeFunctionResult(
                'getPair',
                getPairResult,
              )

            if (
              !pairAddress ||
              pairAddress.toLowerCase() === AddressZero.toLowerCase()
            ) {
              return [PairState.NOT_EXISTS, null] as [
                PairState,
                Pair | null,
              ]
            }

            const reservesData =
              PAIR_INTERFACE.encodeFunctionData('getReserves')

            const reservesResult = await library.call({
              to: pairAddress,
              data: reservesData,
            })

            const decoded =
              PAIR_INTERFACE.decodeFunctionResult(
                'getReserves',
                reservesResult,
              )

            const reserve0 = decoded.reserve0 ?? decoded[0]
            const reserve1 = decoded.reserve1 ?? decoded[1]

            const [token0, token1] = tokenA.sortsBefore(tokenB)
              ? [tokenA, tokenB]
              : [tokenB, tokenA]

            return [
              PairState.EXISTS,
              new Pair(
                new TokenAmount(token0, reserve0.toString()),
                new TokenAmount(token1, reserve1.toString()),
              ),
            ] as [PairState, Pair | null]
          } catch (error) {
            console.error(
              'Robinhood Mainnet pair lookup failed',
              tokenA.address,
              tokenB.address,
              error,
            )

            return [PairState.NOT_EXISTS, null] as [
              PairState,
              Pair | null,
            ]
          }
        }),
      )

      if (!cancelled) {
        setRobinhoodMainnetResults(loaded)
      }
    }

    loadPairs()

    return () => {
      cancelled = true
    }
  }, [chainId, library, tokens])

  if (chainId === ROBINHOOD_MAINNET_CHAIN_ID) {
    return robinhoodMainnetResults.length === tokens.length
      ? robinhoodMainnetResults
      : tokens.map(() => [PairState.LOADING, null])
  }

  return legacyPairResults
}

export function usePair(
  tokenA?: Currency,
  tokenB?: Currency,
): [PairState, Pair | null] {
  return usePairs([[tokenA, tokenB]])[0]
}
